/// <amd-module name="Wiki/Overview/Components/WikiHeader" />
import * as React from "react";

import { isFeatureEnabled } from "VSS/Platform/Feature";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { CustomHeader, HeaderDescription, HeaderTitleArea, HeaderTitleRow } from "VSSUI/Header";
import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { SplitButton } from "VSSUI/SplitButton";
import { css } from "VSSUI/Util";

import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IHeaderMetaDataState } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import * as Resources from "Wiki/Overview/Resources";
import { publishWikiPageCommandClicked } from "Wiki/Overview/Utils/WikiTelemetryHelper";
import { WikiPageMenuItemsProvider } from "Wiki/Overview/WikiPageMenuItemsProvider";
import { RepoServiceContributionId } from "Wiki/ViewCommon/Constants";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName } from "Wiki/ViewCommon/Utils/PathHelper";
import { isCodeEditEnabled, isCodeWiki } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IWikiHeaderProps {
    pagePath: string;
    headerMetaDataState: IHeaderMetaDataState | undefined;
    onUpdateLinkWitClick: (count: number) => void;
    wiki: WikiV2;
    page: WikiPage | undefined;
    menuItemProvider: WikiPageMenuItemsProvider;
    hasWikiContributePermission?: boolean;
    unfollowPage?: boolean;
    refreshFollowStatus?: boolean;
    onFollowStatusRefreshed?: () => void;
    editCurrentPage: () => void;
    redirectToCode: (pagePath: string) => void;
    unsetUnfollowPageOption(): void;
}

const WikiHeaderMenuSource = "header-menu";

export class WikiHeader extends VssComponent<IWikiHeaderProps, {}> {
    public render(): JSX.Element {
        return (
            <div className={"wiki-content-header"}>
                {this.props.page && (
                    <>
                        <CustomHeader className="wiki-header-component">
                            <HeaderTitleArea>
                                <HeaderTitleRow>
                                    <WikiHeaderTitle className="text-ellipsis">{getFileName(this.props.pagePath)}</WikiHeaderTitle>
                                </HeaderTitleRow>
                            </HeaderTitleArea>
                            <div className="flex-row flex-noshrink">
                                {this.props.page && this.props.page.id && (
                                    <WrappedComponent
                                        wrappedType={"Wiki.FollowComponent"}
                                        dependencies={["ms.vss-wiki-web.wiki-page-follow"]}
                                        pageId={this.props.page.id}
                                        wikiId={this.props.wiki.id}
                                        projectId={this.props.wiki.projectId}
                                        unfollowPage={this.props.unfollowPage}
                                        unsetUnfollowPageOption={this.props.unsetUnfollowPageOption}
                                        refreshFollowStatus={this.props.refreshFollowStatus}
                                        onFollowStatusRefreshed={this.props.onFollowStatusRefreshed}
                                    />
                                )}

                                {this._onRenderEditElement()}
                                <HeaderCommandBar
                                    className="wiki-header-command-bar"
                                    items={this.props.menuItemProvider.getHeaderMenuItems(this.props.page)}
                                />
                            </div>
                        </CustomHeader>
                        {this.props.page.isNonConformant ? (
                            <MessageCard
                                severity={MessageCardSeverity.Warning}
                                className={"wiki-message-card"}
                                buttonProps={[
                                    {
                                        text: Resources.PageLearnMoreText,
                                        href: Resources.NonConformancePageLearnMoreLink,
                                        target: "_blank"
                                    }
                                ]}
                            >
                                {Resources.NonConformantPageError}
                            </MessageCard>
                        ) : (
                            this.props.headerMetaDataState && (
                                <HeaderDescription>
                                    <WrappedComponent
                                        wrappedType={"Wiki.OverviewMetaDataHeader"}
                                        dependencies={["ms.vss-wiki-web.wiki-headermetadata-content"]}
                                        {...this.props}
                                    />
                                </HeaderDescription>
                            )
                        )}
                    </>
                )}
            </div>
        );
    }

    private _onRenderEditElement(): JSX.Element | null {
        const btnClassName = "wh-edit-btn";
        const page = this.props.page;
        if (page) {
            if (!isCodeWiki(this.props.wiki)) {
                return (
                    <Button
                        className={btnClassName}
                        text={Resources.EditPageMenuItemText}
                        disabled={!this.props.hasWikiContributePermission || page.isNonConformant}
                        onClick={() => {
                            this._editPageAndPublishTelemetry(page);
                        }}
                    />
                );
            } else {
                const isEditDisabled =
                    !this.props.hasWikiContributePermission ||
                    page.isNonConformant;

                const isEditInReposDisabled = !isFeatureEnabled(this.context.pageContext, RepoServiceContributionId);

                if (!isCodeEditEnabled(this.context.pageContext)) {
                    return (
                        <Button
                            className={btnClassName}
                            text={Resources.EditInReposMenuItemText}
                            disabled={isEditDisabled}
                            onClick={() => {
                                this._editInReposAndPublishTelemetry(page);
                            }}
                        />
                    );
                } else {
                    return (
                        <SplitButton
                            className={btnClassName}
                            disabled={isEditDisabled}
                            buttonProps={{
                                text: Resources.EditPageMenuItemText,
                                onClick: () => {
                                    this._editPageAndPublishTelemetry(page);
                                }
                            }}
                            menuButtonProps={{
                                ariaLabel: Resources.MoreEditOptions_Label,
                                contextualMenuProps: {
                                    menuProps: {
                                        id: "wh-edit-menu",
                                        items: [
                                            {
                                                id: "edit-in-repos",
                                                text: Resources.EditInReposMenuItemText,
                                                iconProps: { iconName: "Edit" },
                                                onActivate: () => {
                                                    this._editInReposAndPublishTelemetry(page);
                                                },
                                                disabled: isEditInReposDisabled
                                            }
                                        ]
                                    }
                                }
                            }}
                        />
                    );
                }
            }
        } else {
            return null;
        }
    }

    private _editPageAndPublishTelemetry(page: WikiPage): void {
        publishWikiPageCommandClicked(
            this.context.pageContext,
            TelemetryConstants.EditButtonClicked,
            WikiHeaderMenuSource,
            this.props.wiki.type,
            page.id,
            TelemetryConstants.ViewMode,
            true
        );
        this.props.editCurrentPage();
    }

    private _editInReposAndPublishTelemetry(page: WikiPage): void {
        publishWikiPageCommandClicked(
            this.context.pageContext,
            TelemetryConstants.EditInReposButtonClicked,
            WikiHeaderMenuSource,
            this.props.wiki.type,
            page.id,
            TelemetryConstants.ViewMode,
            true
        );
        this.props.redirectToCode(page.path);
    }
}

interface IHeaderTitleProps {
    className?: string;
}

function WikiHeaderTitle(props: IHeaderTitleProps & Readonly<{ children: React.ReactNode }>): JSX.Element {
    return (
        <div aria-level={1} className={css(props.className, "wiki-header-title title-l")} role="heading">
            {props.children}
        </div>
    );
}
